import React, { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBed,
  faBus,
  faCar,
  faChevronLeft,
  faChevronRight,
  faCompass,
  faLocationDot,
  faPlane,
  faShip,
  faSun,
  faTrain,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';

/**
 * The itinerary globe's overlays, ported from the app's ItineraryGlobeCard (detail card for a
 * stop, day visit or leg) and ItineraryGlobeExplore (a city's stay and places as a row of cards).
 */

export const SELECTED = '#FF6F61';
const PRIMARY = '#074E75';

/** Same colour key as My map and the trip's Points of interest map. */
export const TONE_COLOR = {
  loved: '#E2725B',
  liked: PRIMARY,
  want: '#2E9E5B',
  visited: '#0A5C8A',
};

const TONE_LABEL = {
  loved: 'Loved it',
  liked: 'Liked it',
  visited: 'Visited',
  want: 'Want to go',
};

export const MODE_ICON = { flight: faPlane, train: faTrain, boat: faShip, bus: faBus, car: faCar };
const MODE_LABEL = { flight: 'Flight', train: 'Train', boat: 'Boat', bus: 'Bus', car: 'Drive' };

const shortDate = (d) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
const formatDuration = (min) => {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h ? (m ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
};

/** Detail card for the clicked stop, day visit or leg. */
export const GlobeCard = ({ model, selection, onSelect, onExplore, onClose }) => {
  const dayLabel = (day, date) => (model.flexible || !date ? `Day ${day}` : `Day ${day} · ${shortDate(date)}`);
  const legTimeLabel = (t) => [t.day != null ? dayLabel(t.day, t.date) : null, t.time].filter(Boolean).join(' · ');
  const legChip = (leg, label) => (
    <button key={leg.id} type="button" className="si-globe-chip" onClick={() => onSelect({ kind: 'leg', id: leg.id })}>
      <FontAwesomeIcon icon={MODE_ICON[leg.mode]} />
      <span>{label}</span>
    </button>
  );

  let body = null;

  if (selection.kind === 'stop') {
    const index = model.stops.findIndex((s) => s.id === selection.id);
    const stop = model.stops[index];
    if (!stop) return null;
    const prev = model.stops[index - 1];
    const next = model.stops[index + 1];
    const dates = stop.dayStart === stop.dayEnd
      ? dayLabel(stop.dayStart, stop.start)
      : model.flexible
        ? `Days ${stop.dayStart} to ${stop.dayEnd}`
        : `${shortDate(stop.start)} to ${shortDate(stop.end)}`;
    const dayVisits = model.dayVisits.filter((d) => d.parentId === stop.id);
    const arrivals = model.legs.filter((l) => l.toStopId === stop.id);
    const departures = model.legs.filter((l) => l.fromStopId === stop.id);
    body = (
      <>
        <div className="si-globe-eyebrow-row">
          <button
            type="button"
            className="si-globe-step"
            disabled={!prev}
            aria-label="Previous stop"
            onClick={() => prev && onSelect({ kind: 'stop', id: prev.id })}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <span className="si-globe-eyebrow">STOP {stop.number} OF {model.stops.length}</span>
          <button
            type="button"
            className="si-globe-step"
            disabled={!next}
            aria-label="Next stop"
            onClick={() => next && onSelect({ kind: 'stop', id: next.id })}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
        <p className="si-globe-title">{stop.name}</p>
        <p className="si-globe-meta">{dates} · {stop.nights > 0 ? plural(stop.nights, 'night') : 'Same day'}</p>
        {(arrivals.length > 0 || departures.length > 0 || dayVisits.length > 0) && (
          <div className="si-globe-chips">
            {arrivals.map((l) => legChip(l, `${MODE_LABEL[l.mode]} from ${l.from.name}`))}
            {dayVisits.map((d) => (
              <button key={d.id} type="button" className="si-globe-chip" onClick={() => onSelect({ kind: 'dayVisit', id: d.id })}>
                <FontAwesomeIcon icon={faSun} />
                <span>{d.name}</span>
              </button>
            ))}
            {departures.map((l) => legChip(l, `${MODE_LABEL[l.mode]} to ${l.to.name}`))}
          </div>
        )}
        {(stop.placeIds.length > 0 || stop.accommodationIds.length > 0) && (
          <button type="button" className="si-globe-explore" onClick={() => onExplore(stop.id)}>
            <FontAwesomeIcon icon={faCompass} />
            <span>
              Explore {stop.name.split(',')[0]}
              {stop.placeIds.length > 0 ? ` · ${plural(stop.placeIds.length, 'place')}` : ''}
            </span>
          </button>
        )}
      </>
    );
  } else if (selection.kind === 'dayVisit') {
    const dv = model.dayVisits.find((d) => d.id === selection.id);
    if (!dv) return null;
    const parent = model.stops.find((s) => s.id === dv.parentId);
    body = (
      <>
        <span className="si-globe-eyebrow">DAY TRIP</span>
        <p className="si-globe-title">{dv.name}</p>
        <p className="si-globe-meta">{dayLabel(dv.day, dv.date)}</p>
        {parent && (
          <div className="si-globe-chips">
            <button type="button" className="si-globe-chip" onClick={() => onSelect({ kind: 'stop', id: parent.id })}>
              <span className="si-globe-stop-number">{parent.number}</span>
              <span>From {parent.name}</span>
            </button>
          </div>
        )}
      </>
    );
  } else if (selection.kind === 'leg') {
    const leg = model.legs.find((l) => l.id === selection.id);
    if (!leg) return null;
    const route = leg.from.code && leg.to.code ? `${leg.from.code} → ${leg.to.code}` : null;
    const dep = legTimeLabel(leg.departure);
    const arr = legTimeLabel(leg.arrival);
    // Same-day arrival: the day is already on the departure side.
    const arrShort = leg.arrival.day === leg.departure.day && leg.arrival.time ? leg.arrival.time : arr;
    body = (
      <>
        <div className="si-globe-eyebrow-row">
          <FontAwesomeIcon icon={MODE_ICON[leg.mode]} className="si-globe-eyebrow-icon" />
          <span className="si-globe-eyebrow">
            {leg.role === 'arrival' ? 'GETTING THERE · ' : leg.role === 'departure' ? 'HEADING HOME · ' : ''}
            {MODE_LABEL[leg.mode].toUpperCase()}{route ? ` · ${route}` : ''}
          </span>
        </div>
        <p className="si-globe-title">{leg.from.name} → {leg.to.name}</p>
        {!!dep && (
          <p className="si-globe-meta">
            {dep}{arrShort && arrShort !== dep ? ` to ${arrShort}` : ''}
            {leg.durationMin ? ` · ${formatDuration(leg.durationMin)}` : ''}
          </p>
        )}
      </>
    );
  }

  // Places and stays show in the Explore row, not this card.
  if (!body) return null;

  return (
    <div className="si-globe-card">
      <button type="button" className="si-globe-close" onClick={onClose} aria-label="Close">
        <FontAwesomeIcon icon={faXmark} />
      </button>
      {body}
    </div>
  );
};

const Thumb = ({ src, icon }) => (
  <span className="si-globe-thumb">
    {src ? <img src={src} alt="" /> : <FontAwesomeIcon icon={icon} />}
  </span>
);

/** One card plus the gap: the row's scroll step. */
const stepWidth = (row) => {
  const card = row?.firstElementChild;
  if (!card) return 1;
  return card.getBoundingClientRect().width + parseFloat(getComputedStyle(row).columnGap || '0');
};
const scrollRowTo = (row, index) => row?.scrollTo({ left: index * stepWidth(row), behavior: 'smooth' });

/**
 * A city's stay and places as a scrolling row of cards, kept in step with the map: a card that
 * settles after scrolling becomes the selection, and a pin clicked on the map (activeId) scrolls
 * its card into view. Clicking the active card again steps back out to the city.
 */
export const GlobeExplore = ({ items, activeId, onActiveChange }) => {
  const rowRef = useRef(null);
  // Index the row is showing, so a scroll we started doesn't echo back as a choice.
  const shownIndex = useRef(0);
  const settleTimer = useRef(null);

  const scrollToIndex = (index) => scrollRowTo(rowRef.current, index);

  useEffect(() => {
    const index = items.findIndex((it) => it.id === activeId);
    if (index < 0 || index === shownIndex.current) return;
    shownIndex.current = index;
    scrollRowTo(rowRef.current, index);
  }, [activeId, items]);

  useEffect(() => () => clearTimeout(settleTimer.current), []);

  if (items.length === 0) return null;

  const onScroll = () => {
    clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(() => {
      const row = rowRef.current;
      if (!row) return;
      const index = Math.max(0, Math.min(items.length - 1, Math.round(row.scrollLeft / stepWidth(row))));
      // Settling back on the same card is not a choice (a click on it is).
      if (index === shownIndex.current) return;
      shownIndex.current = index;
      onActiveChange(items[index].id);
    }, 140);
  };

  const go = (delta) => {
    const index = Math.max(0, Math.min(items.length - 1, shownIndex.current + delta));
    if (index === shownIndex.current) return;
    shownIndex.current = index;
    scrollToIndex(index);
    onActiveChange(items[index].id);
  };

  return (
    <div className="si-globe-explore-wrap">
      {items.length > 1 && (
        <button type="button" className="si-globe-row-arrow si-globe-row-prev" aria-label="Previous" onClick={() => go(-1)}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
      )}
      <div ref={rowRef} className="si-globe-explore-row" onScroll={onScroll}>
        {items.map((item, index) => (
          <button
            key={`${item.kind}-${item.id}`}
            type="button"
            className={`si-globe-explore-card${item.id === activeId ? ' si-globe-explore-card-active' : ''}`}
            onClick={() => {
              shownIndex.current = index;
              scrollToIndex(index);
              onActiveChange(item.id);
            }}
          >
            {item.kind === 'stay' ? (
              <>
                <Thumb src={item.stay.photoUrl} icon={faBed} />
                <span className="si-globe-explore-body">
                  <span className="si-globe-explore-eyebrow" style={{ color: PRIMARY }}>STAY</span>
                  <span className="si-globe-explore-title">{item.stay.name}</span>
                  {item.stay.nights > 0 && <span className="si-globe-explore-meta">{plural(item.stay.nights, 'night')}</span>}
                </span>
              </>
            ) : (
              <>
                <Thumb src={item.place.thumbnailUrl} icon={faLocationDot} />
                <span className="si-globe-explore-body">
                  <span className="si-globe-explore-eyebrow" style={{ color: TONE_COLOR[item.place.tone] }}>
                    {TONE_LABEL[item.place.tone].toUpperCase()}
                  </span>
                  <span className="si-globe-explore-title">{item.place.name}</span>
                </span>
              </>
            )}
          </button>
        ))}
      </div>
      {items.length > 1 && (
        <button type="button" className="si-globe-row-arrow si-globe-row-next" aria-label="Next" onClick={() => go(1)}>
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      )}
    </div>
  );
};
